import { ReactElement } from 'react';

import {
   MedicalDataAccessAuthorization,
   PatientConsentDocument,
   PatientConsentDocumentQuery,
   ValidMedicalDataAccessAuthorizationQuery,
} from '@dreryk/egabinet-patient-frontend-api';
import { PatientId } from '@dreryk/egabinet-patient-published-language-api';

import { useCqrsSearch } from '@dreryk/command-query-react-common';
import { commonTranslationKeys } from '@dreryk/react-common';
import { Message, MessageType } from '@dreryk/react-components';

import { SidePanelLoader } from '../SidePanelLoader';

type PatientConsentsAndAuthorizationsSidePanelListLoaderRenderer = (renderProps: {
   patientConsents: PatientConsentDocument[];
   patientAuthorizations: MedicalDataAccessAuthorization[];
}) => ReactElement;

type PatientConsentsAndAuthorizationsSidePanelListLoaderProps = {
   readonly patientId: number;
   readonly children: PatientConsentsAndAuthorizationsSidePanelListLoaderRenderer;
};

export const PatientConsentsAndAuthorizationsSidePanelListLoader = (
   props: PatientConsentsAndAuthorizationsSidePanelListLoaderProps
): ReactElement => {
   const {
      data: patientConsentDocuments,
      loading: patientConsentDocumentsLoading,
      error: patientConsentDocumentsError,
   } = useCqrsSearch<PatientConsentDocument>(() => {
      const query = new PatientConsentDocumentQuery();
      query.patientId = props.patientId;
      return query;
   });

   const {
      data: patientMedicalDataAuthorizations,
      loading: patientMedicalDataAuthorizationsLoading,
      error: patientMedicalDataAuthorizationsDocumentsError,
   } = useCqrsSearch<MedicalDataAccessAuthorization>(() => {
      const query = new ValidMedicalDataAccessAuthorizationQuery();
      query.patientId = PatientId.of(props.patientId);
      return query;
   });

   if (patientConsentDocumentsLoading || patientMedicalDataAuthorizationsLoading) {
      return <SidePanelLoader />;
   }

   if (patientConsentDocumentsError || patientMedicalDataAuthorizationsDocumentsError) {
      return (
         <Message
            testId={'fetchError'}
            type={MessageType.NEGATIVE}
            label={commonTranslationKeys.unexpectedErrorHasOccurred}
            displayIndicator
         />
      );
   }

   return props.children({
      patientConsents: patientConsentDocuments,
      patientAuthorizations: patientMedicalDataAuthorizations,
   });
};
